import { IProduct, IProductVariantSizeConfig, IProductVariantSummary } from '../store/slices/productSlice';
import { CartItem, CartItemSnapshot } from '../types/cart';

export const normalizeNumber = (value: number | string | null | undefined): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const normalized = Number(value.replace(',', '.').trim());
    return Number.isFinite(normalized) ? normalized : null;
  }
  return null;
};

export const formatCurrency = (value: number | null) => {
  if (value === null) {
    return '\u041f\u043e \u0437\u0430\u043f\u0440\u043e\u0441\u0443';
  }

  return `${new Intl.NumberFormat('ru-RU').format(value)} \u20bd`;
};

const getVariantById = (product: IProduct, variantId?: string) => {
  const variants = Array.isArray(product.variants) ? product.variants : [];
  if (!variants.length) {
    return null;
  }

  return variants.find((variant) => String(variant.id) === String(variantId)) || variants[0];
};

const getSizeConfig = (variant: IProductVariantSummary | null, size?: string): IProductVariantSizeConfig | null => {
  if (!variant || !Array.isArray(variant.sizeConfigs) || !size) {
    return null;
  }

  return variant.sizeConfigs.find((config) => config.size === size) || null;
};

const getSharedSizeConfig = (product: IProduct, size?: string): IProductVariantSizeConfig | null => {
  if (!size || !Array.isArray(product.variants)) {
    return null;
  }

  let mergedConfig: IProductVariantSizeConfig | null = null;

  product.variants.forEach((variant) => {
    const sizeConfig = getSizeConfig(variant, size);
    if (!sizeConfig) {
      return;
    }

    if (!mergedConfig) {
      mergedConfig = {
        ...sizeConfig,
      };
      return;
    }

    (['weight_grams', 'package_length_cm', 'package_width_cm', 'package_height_cm'] as const).forEach((field) => {
      const currentValue = String(mergedConfig?.[field] ?? '').trim();
      const nextValue = String(sizeConfig[field] ?? '').trim();
      if (!currentValue && nextValue) {
        mergedConfig = {
          ...mergedConfig!,
          [field]: sizeConfig[field],
        };
      }
    });
  });

  return mergedConfig;
};

const normalizeShippingSnapshot = (
  source: Partial<Record<keyof CartItemSnapshot, number | string | null | undefined>> | null | undefined,
): CartItemSnapshot => ({
  weight_grams: normalizeNumber(source?.weight_grams),
  package_length_cm: normalizeNumber(source?.package_length_cm),
  package_width_cm: normalizeNumber(source?.package_width_cm),
  package_height_cm: normalizeNumber(source?.package_height_cm),
});

export const resolveShippingSnapshot = (
  product: IProduct,
  options: { variantId?: string; variantName?: string; size?: string },
): CartItemSnapshot => {
  return normalizeShippingSnapshot(getSharedSizeConfig(product, options.size));
};

export const getSelectedVariantSku = (
  product: Pick<IProduct, 'variants'>,
  selection: { variantId?: string; variantName?: string; size?: string },
): string => {
  if (!selection.size) return '';

  const variants = Array.isArray(product.variants) ? product.variants : [];
  const variant = selection.variantId
    ? variants.find((item) => String(item.id) === String(selection.variantId))
    : selection.variantName
      ? variants.find((item) => item.name === selection.variantName)
      : variants.length === 1 ? variants[0] : undefined;

  const config = variant?.sizeConfigs?.find((item) => item.size === selection.size);
  return typeof config?.sku === 'string' ? config.sku.trim() : '';
};

export const getProductSku = (
  product: Pick<IProduct, 'sku' | 'card_specs' | 'variants'>,
  selection?: { variantId?: string; variantName?: string; size?: string },
) => {
  // Артикул варианта не подменяется кодом всей карточки.
  if (selection?.size) return getSelectedVariantSku(product, selection);

  if (product.sku && product.sku.trim()) {
    return product.sku.trim();
  }

  const specs = product.card_specs || {};
  const skuEntry = Object.entries(specs).find(([key]) => /sku|article/i.test(key));
  return skuEntry ? String(skuEntry[1]) : '';
};

export const getAvailableSizes = (product: IProduct, variantId?: string) => {
  const variant = getVariantById(product, variantId);
  return Array.isArray(variant?.sizes) ? variant?.sizes || [] : [];
};

const getPriceTiers = (variant: IProductVariantSummary | null, size?: string) => {
  if (!variant || !Array.isArray(variant.sizeConfigs) || !size) {
    return [];
  }

  const sizeConfig = variant.sizeConfigs.find((config) => config.size === size);
  if (!sizeConfig?.priceTiers) {
    return [];
  }

  return sizeConfig.priceTiers
    .map((tier) => ({
      minQuantity: normalizeNumber(tier.minQuantity) || 1,
      price: normalizeNumber(tier.price),
    }))
    .filter((tier): tier is { minQuantity: number; price: number } => tier.price !== null)
    .sort((a, b) => a.minQuantity - b.minQuantity);
};

const resolvePriceFromTiers = (priceTiers: Array<{ minQuantity: number; price: number }>, quantity: number) => {
  if (!priceTiers.length) {
    return null;
  }

  return [...priceTiers].reverse().find((tier) => quantity >= tier.minQuantity)?.price || priceTiers[0].price;
};

export const resolveUnitPrice = (
  product: IProduct,
  options: { variantId?: string; size?: string },
  quantity = 1,
) => {
  const variant = getVariantById(product, options.variantId);
  const priceTiers = getPriceTiers(variant, options.size);
  const variantPrice = resolvePriceFromTiers(priceTiers, quantity);
  if (variantPrice !== null) {
    return variantPrice;
  }

  const productPrice = normalizeNumber(product.price);
  if (productPrice !== null) {
    return productPrice;
  }

  return null;
};

export const getPriceTiersForSelection = (product: IProduct, options: { variantId?: string; size?: string }) => {
  const variant = getVariantById(product, options.variantId);
  return getPriceTiers(variant, options.size);
};

export const buildCartItemKey = (productId: string, options: Record<string, string>) => {
  const normalizedOptions = Object.entries(options)
    .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
    .map(([key, value]) => `${key}:${value}`)
    .join('|');

  return normalizedOptions ? `${productId}::${normalizedOptions}` : productId;
};

export const buildCartItemFromProduct = (
  product: IProduct,
  selection: { variantId?: string; size?: string },
  quantity = 1,
): CartItem => {
  const variant = getVariantById(product, selection.variantId);
  const priceTiers = getPriceTiersForSelection(product, selection);
  const unitPrice = resolveUnitPrice(product, selection, quantity);
  const options: Record<string, string> = {};

  if (variant?.name) {
    options.variant = variant.name;
  }

  if (selection.size) {
    options.size = selection.size;
  }

  return {
    key: buildCartItemKey(product.id, options),
    productId: product.id,
    name: product.name,
    sku: getProductSku(product, selection),
    image: variant?.image || variant?.images?.[0] || product.image || '/images/placeholder.jpg',
    options,
    quantity,
    unitPrice,
    lineTotal: unitPrice !== null ? Number((unitPrice * quantity).toFixed(2)) : null,
    priceTiers,
    itemSnapshot: resolveShippingSnapshot(product, selection),
  };
};
